/**
 * Store locator voor Back on Track.
 *
 * Haalt de dealers op via de Storefront API (metaobjects van het type "store"),
 * bewaart ze 24 uur in localStorage en tekent ze op een Google kaart met
 * clustering. Geen build stap, geen dependencies buiten Google Maps en de
 * marker clusterer.
 */
(function () {
  'use strict';

  var CACHE_PREFIX = 'bot:store-locator:';
  var CACHE_VERSION = 'v2';
  var CACHE_TTL = 24 * 60 * 60 * 1000;
  var PAGE_SIZE = 250;
  var CLUSTERER_SRC =
    'https://cdn.jsdelivr.net/npm/@googlemaps/markerclusterer@2.5.3/dist/index.min.js';
  var EUROPE = { lat: 50.5, lng: 7.0 };

  // Het gebied waar Back on Track dealers heeft. Sinds de lijst van oktober 2026
  // ook Italie, Spanje, het Verenigd Koninkrijk, Tsjechie, Slowakije, Polen,
  // Hongarije en Liechtenstein, dus de rechthoek loopt van Zuid-Spanje tot
  // Schotland en tot voorbij Warschau. Buiten dit vak stellen we niets voor en
  // zoeken we niet, anders belandt iemand die "Paris" typt in Paris, Texas.
  var SERVICE_AREA = { south: 35.0, west: -10.5, north: 60.0, east: 25.5 };

  // Levert een zoekopdracht niets op binnen de straal, dan tonen we er zoveel
  // buiten de straal. Een lege pagina is altijd een doodlopende weg.
  var FALLBACK_COUNT = 5;
  var EUROPE_ZOOM = 5;
  var MAX_ONDEMAND_GEOCODES = 10;

  /* ---------------------------------------------------------- hulpjes -- */

  function toRad(deg) {
    return (deg * Math.PI) / 180;
  }

  /** Hemelsbrede afstand in kilometers. */
  function haversine(lat1, lng1, lat2, lng2) {
    var R = 6371;
    var dLat = toRad(lat2 - lat1);
    var dLng = toRad(lng2 - lng1);
    var a =
      Math.sin(dLat / 2) * Math.sin(dLat / 2) +
      Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLng / 2) * Math.sin(dLng / 2);
    return 2 * R * Math.asin(Math.min(1, Math.sqrt(a)));
  }

  function el(tag, className, text) {
    var node = document.createElement(tag);
    if (className) node.className = className;
    if (text !== undefined && text !== null && text !== '') node.textContent = text;
    return node;
  }

  /** Vervangt __placeholders__ in een vertaalde string. */
  function fill(template, values) {
    var out = String(template || '');
    Object.keys(values || {}).forEach(function (key) {
      out = out.split('__' + key + '__').join(values[key]);
    });
    return out;
  }

  function loadScript(src) {
    return new Promise(function (resolve, reject) {
      var existing = document.querySelector('script[src="' + src + '"]');
      if (existing) {
        if (existing.dataset.loaded === 'true') return resolve();
        existing.addEventListener('load', function () {
          resolve();
        });
        existing.addEventListener('error', reject);
        return;
      }
      var script = document.createElement('script');
      script.src = src;
      script.async = true;
      script.addEventListener('load', function () {
        script.dataset.loaded = 'true';
        resolve();
      });
      script.addEventListener('error', function () {
        reject(new Error('Kon script niet laden: ' + src));
      });
      document.head.appendChild(script);
    });
  }

  /* ------------------------------------------------------ Google Maps -- */

  var mapsPromise = null;

  function loadGoogleMaps(key, language) {
    if (mapsPromise) return mapsPromise;
    if (window.google && window.google.maps && window.google.maps.Map) {
      mapsPromise = Promise.resolve();
      return mapsPromise;
    }
    mapsPromise = new Promise(function (resolve, reject) {
      var callbackName = '__storeLocatorMapsReady';
      window[callbackName] = function () {
        resolve();
        delete window[callbackName];
      };
      var params = [
        'key=' + encodeURIComponent(key),
        'libraries=places',
        'loading=async',
        'callback=' + callbackName
      ];
      if (language) params.push('language=' + encodeURIComponent(language));
      var script = document.createElement('script');
      script.src = 'https://maps.googleapis.com/maps/api/js?' + params.join('&');
      script.async = true;
      script.addEventListener('error', function () {
        reject(new Error('Google Maps kon niet laden'));
      });
      document.head.appendChild(script);
    });
    return mapsPromise;
  }

  /* --------------------------------------------------------- de klasse -- */

  function StoreLocator(root) {
    this.root = root;
    this.shopDomain = root.dataset.shopDomain;
    this.apiVersion = root.dataset.apiVersion || '2025-10';
    this.token = root.dataset.storefrontToken;
    this.mapsKey = root.dataset.mapsKey;
    this.perPage = parseInt(root.dataset.perPage, 10) || 25;
    this.radius = parseInt(root.dataset.radius, 10) || 20;
    this.language = (root.dataset.locale || 'en').split('-')[0];

    this.i18n = this.readStrings();

    this.form = root.querySelector('[data-sl-form]');
    this.queryInput = root.querySelector('[data-sl-query]');
    this.radiusSelect = root.querySelector('[data-sl-radius]');
    this.locateButton = root.querySelector('[data-sl-locate]');
    this.statusNode = root.querySelector('[data-sl-status]');
    this.listNode = root.querySelector('[data-sl-list]');
    this.emptyNode = root.querySelector('[data-sl-empty]');
    this.moreButton = root.querySelector('[data-sl-more]');
    this.mapNode = root.querySelector('[data-sl-map]');
    this.offerNode = root.querySelector('[data-sl-offer]');
    this.viewsNode = root.querySelector('[data-sl-views]');

    this.stores = [];
    this.results = [];
    this.shown = 0;
    this.origin = null;
    this.markers = new Map();
    this.clusterer = null;
    this.map = null;
    this.infoWindow = null;
    this.selectedPlace = null;
    this.nameHits = [];
    this.geocodeBudget = MAX_ONDEMAND_GEOCODES;

    this.root.dataset.view = 'list';

    this.bindEvents();
    this.start();
  }

  StoreLocator.prototype.readStrings = function () {
    var node = this.root.querySelector('[data-sl-i18n]');
    if (!node) return {};
    try {
      return JSON.parse(node.textContent);
    } catch (err) {
      return {};
    }
  };

  StoreLocator.prototype.t = function (key, values) {
    return fill(this.i18n[key] !== undefined ? this.i18n[key] : key, values);
  };

  /** Kiest de enkelvouds- of meervoudsvorm van een vertaling. */
  StoreLocator.prototype.tCount = function (key, count, extra) {
    var values = { count: count };
    Object.keys(extra || {}).forEach(function (name) {
      values[name] = extra[name];
    });
    return this.t(key + (count === 1 ? '_one' : '_other'), values);
  };

  /** Ligt dit punt in het gebied waar we dealers hebben? */
  StoreLocator.prototype.inServiceArea = function (lat, lng) {
    return (
      lat >= SERVICE_AREA.south &&
      lat <= SERVICE_AREA.north &&
      lng >= SERVICE_AREA.west &&
      lng <= SERVICE_AREA.east
    );
  };

  StoreLocator.prototype.setStatus = function (text) {
    if (!this.statusNode) return;
    // Zonder sleutel blijft de kaart leeg. Die melding moet zichtbaar blijven,
    // anders wordt hij meteen overschreven door de laad- of resultaattekst.
    var suffix = this.mapsKey ? '' : ' ' + this.t('no_key');
    this.statusNode.textContent = (text || '') + suffix;
  };

  /* -------------------------------------------------------- events ----- */

  StoreLocator.prototype.bindEvents = function () {
    var self = this;

    if (this.form) {
      this.form.addEventListener('submit', function (event) {
        event.preventDefault();
        self.search(self.queryInput ? self.queryInput.value : '');
      });
    }

    if (this.queryInput) {
      // Een nieuwe tekst maakt een eerder gekozen suggestie ongeldig.
      this.queryInput.addEventListener('input', function () {
        self.selectedPlace = null;
      });
    }

    if (this.radiusSelect) {
      this.radiusSelect.addEventListener('change', function () {
        self.radius = parseInt(self.radiusSelect.value, 10) || self.radius;
        if (!self.origin) return;
        self.applyResults();
        var query = self.queryInput ? self.queryInput.value.trim() : '';
        if (query) self.writeUrl(query);
      });
    }

    if (this.locateButton) {
      this.locateButton.addEventListener('click', function () {
        self.locate({ silent: false });
      });
    }

    if (this.moreButton) {
      this.moreButton.addEventListener('click', function () {
        self.renderMore();
      });
    }

    this.root.querySelectorAll('[data-sl-view]').forEach(function (button) {
      button.addEventListener('click', function () {
        self.setView(button.dataset.slView);
      });
    });
  };

  /* --------------------------------------------------------- opstarten -- */

  /** Leest ?q= en ?radius= uit de URL, zodat een zoekopdracht deelbaar is. */
  StoreLocator.prototype.readUrl = function () {
    var params = new URLSearchParams(window.location.search);
    var query = (params.get('q') || '').trim();
    var radius = parseInt(params.get('radius'), 10);

    if (radius && this.radiusSelect) {
      var allowed = Array.prototype.map.call(this.radiusSelect.options, function (option) {
        return parseInt(option.value, 10);
      });
      if (allowed.indexOf(radius) > -1) {
        this.radius = radius;
        this.radiusSelect.value = String(radius);
      }
    }
    if (query && this.queryInput) this.queryInput.value = query;
    return query;
  };

  /**
   * Zet de huidige zoekopdracht in de URL. Andere parameters blijven staan,
   * zodat campagnetags niet sneuvelen. Coordinaten uit geolocatie zetten we er
   * bewust niet in.
   */
  StoreLocator.prototype.writeUrl = function (query) {
    if (!window.history || !window.history.replaceState) return;
    var params = new URLSearchParams(window.location.search);
    if (query) {
      params.set('q', query);
      params.set('radius', String(this.radius));
    } else {
      params.delete('q');
      params.delete('radius');
    }
    var search = params.toString();
    window.history.replaceState(
      null,
      '',
      window.location.pathname + (search ? '?' + search : '') + window.location.hash
    );
  };

  StoreLocator.prototype.start = function () {
    var self = this;

    this.setStatus(this.t('loading'));

    this.fetchStores()
      .then(function (stores) {
        self.stores = stores;
        return self.mapsKey ? self.initMap() : null;
      })
      .then(function () {
        self.showAll();
        var query = self.readUrl();
        if (query) {
          self.search(query);
        } else {
          self.locate({ silent: true });
        }
      })
      .catch(function (err) {
        console.error('[store-locator]', err);
        self.setStatus(self.t('load_error'));
      });
  };

  /* ------------------------------------------------------------ data --- */

  StoreLocator.prototype.cacheKey = function () {
    return CACHE_PREFIX + CACHE_VERSION + ':' + this.shopDomain + ':' + this.language;
  };

  StoreLocator.prototype.readCache = function () {
    try {
      var raw = window.localStorage.getItem(this.cacheKey());
      if (!raw) return null;
      var parsed = JSON.parse(raw);
      if (!parsed || parsed.version !== CACHE_VERSION) return null;
      if (Date.now() - parsed.savedAt > CACHE_TTL) return null;
      if (!Array.isArray(parsed.stores) || !parsed.stores.length) return null;
      return parsed.stores;
    } catch (err) {
      return null;
    }
  };

  StoreLocator.prototype.writeCache = function (stores) {
    try {
      window.localStorage.setItem(
        this.cacheKey(),
        JSON.stringify({ version: CACHE_VERSION, savedAt: Date.now(), stores: stores })
      );
    } catch (err) {
      // Prive modus of vol quotum: dan draaien we gewoon zonder cache.
    }
  };

  StoreLocator.prototype.fetchStores = function () {
    var cached = this.readCache();
    if (cached) return Promise.resolve(cached);

    var self = this;
    var query = [
      'query StoreLocatorStores($cursor: String) {',
      '  metaobjects(type: "store", first: ' + PAGE_SIZE + ', after: $cursor) {',
      '    nodes { id handle fields { key value } }',
      '    pageInfo { hasNextPage endCursor }',
      '  }',
      '}'
    ].join('\n');

    var collected = [];

    function page(cursor) {
      return fetch('https://' + self.shopDomain + '/api/' + self.apiVersion + '/graphql.json', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Shopify-Storefront-Access-Token': self.token
        },
        body: JSON.stringify({ query: query, variables: { cursor: cursor || null } })
      })
        .then(function (response) {
          if (!response.ok) throw new Error('Storefront API HTTP ' + response.status);
          return response.json();
        })
        .then(function (body) {
          if (body.errors && body.errors.length) {
            throw new Error(body.errors.map(function (e) { return e.message; }).join(' | '));
          }
          var connection = body.data && body.data.metaobjects;
          if (!connection) throw new Error('Geen metaobjects in het antwoord');
          connection.nodes.forEach(function (node) {
            var store = self.mapStore(node);
            if (store) collected.push(store);
          });
          if (connection.pageInfo.hasNextPage) return page(connection.pageInfo.endCursor);
          return collected;
        });
    }

    return page(null).then(function (stores) {
      self.writeCache(stores);
      return stores;
    });
  };

  /** Metaobject naar een plat object. Lege waarden worden lege strings. */
  StoreLocator.prototype.mapStore = function (node) {
    var values = {};
    (node.fields || []).forEach(function (field) {
      values[field.key] = field.value === null ? '' : field.value;
    });

    // Een leeg vinkje telt als zichtbaar, zodat een handmatig toegevoegde
    // winkel niet per ongeluk onzichtbaar blijft.
    if (values.active === 'false') return null;
    if (!values.name) return null;

    var lat = parseFloat(values.latitude);
    var lng = parseFloat(values.longitude);

    return {
      id: node.handle || node.id,
      name: values.name,
      address: values.address || '',
      address2: values.address_2 || '',
      postalCode: values.postal_code || '',
      city: values.city || '',
      country: values.country || '',
      phone: values.phone || '',
      email: values.email || '',
      website: values.website || '',
      lat: isFinite(lat) ? lat : null,
      lng: isFinite(lng) ? lng : null
    };
  };

  /* ------------------------------------------------------------ kaart -- */

  StoreLocator.prototype.initMap = function () {
    var self = this;
    return loadGoogleMaps(this.mapsKey, this.language)
      .then(function () {
        self.map = new google.maps.Map(self.mapNode, {
          center: EUROPE,
          zoom: EUROPE_ZOOM,
          mapTypeControl: false,
          streetViewControl: false,
          fullscreenControl: true,
          gestureHandling: 'cooperative'
        });
        self.infoWindow = new google.maps.InfoWindow({ maxWidth: 320 });
        self.geocoder = new google.maps.Geocoder();
        self.setupAutocomplete();
        if (self.viewsNode) self.viewsNode.hidden = false;
        return loadScript(CLUSTERER_SRC).catch(function (err) {
          // Zonder clusterer werkt de kaart nog steeds, alleen drukker.
          console.warn('[store-locator] clusterer niet geladen', err);
        });
      })
      .catch(function (err) {
        console.error('[store-locator]', err);
        self.setStatus(self.t('maps_error'));
        throw err;
      });
  };

  /**
   * Places autocomplete is een extraatje. Werkt het niet, dan geocoden we de
   * ingetypte tekst gewoon bij het versturen van het formulier.
   */
  StoreLocator.prototype.setupAutocomplete = function () {
    if (!this.queryInput) return;
    if (!window.google || !google.maps.places || !google.maps.places.Autocomplete) return;

    var self = this;
    try {
      var area = new google.maps.LatLngBounds(
        { lat: SERVICE_AREA.south, lng: SERVICE_AREA.west },
        { lat: SERVICE_AREA.north, lng: SERVICE_AREA.east }
      );
      var autocomplete = new google.maps.places.Autocomplete(this.queryInput, {
        fields: ['geometry', 'formatted_address', 'name'],
        types: ['geocode'],
        bounds: area,
        strictBounds: true
      });
      autocomplete.addListener('place_changed', function () {
        var place = autocomplete.getPlace();
        if (!place || !place.geometry || !place.geometry.location) {
          self.selectedPlace = null;
          return;
        }
        self.selectedPlace = {
          lat: place.geometry.location.lat(),
          lng: place.geometry.location.lng(),
          label: place.formatted_address || place.name || self.queryInput.value
        };
        self.search(self.queryInput.value);
      });
    } catch (err) {
      console.warn('[store-locator] autocomplete niet beschikbaar', err);
    }
  };

  /* ---------------------------------------------------------- zoeken --- */

  /** Kleine letters, accenten eraf, zodat "Tierärzte" ook op "tierarzte" matcht. */
  function foldText(value) {
    return String(value || '')
      .toLowerCase()
      .normalize('NFD')
      .replace(/[̀-ͯ]/g, '')
      .replace(/[^a-z0-9]+/g, ' ')
      .trim();
  }

  /**
   * Zoekt winkels op naam, lokaal en dus zonder Google. Alle woorden uit de
   * zoekterm moeten in de naam voorkomen, zodat "equiva aachen" wel die ene
   * vestiging vindt en niet alle 46 Equiva's.
   */
  StoreLocator.prototype.findByName = function (query) {
    var needle = foldText(query);
    if (needle.length < 3) return [];
    var words = needle.split(' ').filter(Boolean);
    return this.stores.filter(function (store) {
      var hay = foldText(store.name);
      return words.every(function (w) {
        return hay.indexOf(w) > -1;
      });
    });
  };

  /**
   * Toont alleen de naamtreffers. Dit is het geval waarin Google de ingetypte
   * tekst niet als plaats herkent, bijvoorbeeld bij "Pierdhus".
   */
  StoreLocator.prototype.showNameOnly = function (hits, query) {
    this.origin = null;
    this.nameHits = hits;
    this.results = hits.map(function (s) {
      return Object.assign({}, s, { byName: true });
    });
    this.shown = 0;
    this.renderList(true);
    this.renderMarkers(this.results);
    this.fitTo(this.results, false);
    if (this.offerNode) this.offerNode.hidden = true;
    this.setStatus(this.tCount('name_matches', hits.length, { query: query }));
  };



  StoreLocator.prototype.search = function (text) {
    var self = this;
    var value = String(text || '').trim();

    // Eerst lokaal op naam zoeken. Dat kost geen Google-aanroep en werkt ook
    // als de ingetypte tekst helemaal geen plaats is.
    var hits = value ? this.findByName(value) : [];

    if (this.selectedPlace) {
      var place = this.selectedPlace;
      this.nameHits = hits;
      this.writeUrl(value);
      this.setOrigin(place.lat, place.lng, place.label);
      return;
    }

    if (!value) {
      this.origin = null;
      this.nameHits = [];
      this.writeUrl('');
      this.showAll();
      return;
    }

    if (!this.geocoder) {
      this.setStatus(this.t('maps_error'));
      return;
    }

    this.setStatus(this.t('loading'));

    var area = new google.maps.LatLngBounds(
      { lat: SERVICE_AREA.south, lng: SERVICE_AREA.west },
      { lat: SERVICE_AREA.north, lng: SERVICE_AREA.east }
    );

    this.geocoder.geocode({ address: value, bounds: area }, function (results, status) {
      if (status !== 'OK' || !results || !results.length) {
        if (hits.length) return self.showNameOnly(hits, value);
        self.setStatus(self.t('not_found'));
        return;
      }

      // Een plaats buiten ons gebied levert gegarandeerd niets op. Dat zeggen we
      // liever eerlijk dan dat we een lege lijst tonen.
      var inside = results.filter(function (result) {
        return self.inServiceArea(result.geometry.location.lat(), result.geometry.location.lng());
      });
      if (!inside.length) {
        if (hits.length) return self.showNameOnly(hits, value);
        self.setStatus(self.t('outside_area'));
        return;
      }

      var best = inside[0];
      self.nameHits = hits;
      self.writeUrl(value);
      self.setOrigin(
        best.geometry.location.lat(),
        best.geometry.location.lng(),
        best.formatted_address || value
      );
    });
  };

  StoreLocator.prototype.locate = function (options) {
    var self = this;
    var silent = options && options.silent;

    if (!navigator.geolocation) {
      if (!silent) this.setStatus(this.t('geolocation_unavailable'));
      return;
    }

    function request() {
      if (!silent) self.setStatus(self.t('locating'));
      navigator.geolocation.getCurrentPosition(
        function (position) {
          if (self.queryInput) self.queryInput.value = '';
          self.selectedPlace = null;
          self.nameHits = [];
          // Geen coordinaten in de URL, dat is de locatie van de bezoeker.
          self.writeUrl('');
          self.setOrigin(position.coords.latitude, position.coords.longitude, null);
        },
        function (error) {
          if (silent) return;
          self.setStatus(
            error && error.code === error.PERMISSION_DENIED
              ? self.t('geolocation_denied')
              : self.t('geolocation_unavailable')
          );
        },
        { enableHighAccuracy: false, timeout: 10000, maximumAge: 10 * 60 * 1000 }
      );
    }

    // Bij het laden van de pagina vragen we niets: alleen als de bezoeker de
    // toestemming al eerder gaf halen we de locatie stilletjes op.
    if (silent) {
      if (!navigator.permissions || !navigator.permissions.query) return;
      navigator.permissions
        .query({ name: 'geolocation' })
        .then(function (result) {
          if (result.state === 'granted') request();
        })
        .catch(function () {});
      return;
    }
    request();
  };

  StoreLocator.prototype.setOrigin = function (lat, lng, label) {
    this.origin = { lat: lat, lng: lng, label: label };
    this.applyResults();
  };

  /* -------------------------------------------------------- resultaten -- */

  StoreLocator.prototype.showAll = function () {
    this.origin = null;
    this.nameHits = [];
    if (this.offerNode) this.offerNode.hidden = true;
    var list = this.stores.slice().sort(function (a, b) {
      return a.name.localeCompare(b.name);
    });
    this.results = list;
    this.shown = 0;
    this.renderList(true);
    this.renderMarkers(list);
    this.fitTo(list, true);
    this.setStatus(this.tCount('all_stores', list.length));
  };

  StoreLocator.prototype.applyResults = function () {
    var self = this;
    var origin = this.origin;
    if (!origin) return this.showAll();

    var withDistance = [];
    var missing = [];

    this.stores.forEach(function (store) {
      if (store.lat === null || store.lng === null) {
        missing.push(store);
        return;
      }
      var distance = haversine(origin.lat, origin.lng, store.lat, store.lng);
      if (distance <= self.radius) {
        withDistance.push(Object.assign({}, store, { distance: distance }));
      }
    });

    withDistance.sort(function (a, b) {
      return a.distance - b.distance;
    });

    // Naamtreffers gaan bovenaan en krijgen een afstand als we een punt hebben.
    var named = (this.nameHits || []).map(function (store) {
      var extra = { byName: true };
      if (store.lat !== null && store.lng !== null) {
        extra.distance = haversine(origin.lat, origin.lng, store.lat, store.lng);
      }
      return Object.assign({}, store, extra);
    });
    var namedIds = {};
    named.forEach(function (store) { namedIds[store.id] = true; });

    var results = named.concat(withDistance.filter(function (store) {
      return !namedIds[store.id];
    }));
    var fellBack = false;

    // Niets binnen de straal, maar wel dealers verderop: toon de dichtstbijzijnde
    // in plaats van een lege pagina. Rond Brussel zit de dichtstbijzijnde dealer
    // op bijna 70 km, dus zonder deze terugval loopt die bezoeker vast.
    if (!results.length) {
      var all = [];
      this.stores.forEach(function (store) {
        if (store.lat === null || store.lng === null) return;
        all.push(
          Object.assign({}, store, {
            distance: haversine(origin.lat, origin.lng, store.lat, store.lng)
          })
        );
      });
      all.sort(function (a, b) {
        return a.distance - b.distance;
      });
      results = all.slice(0, FALLBACK_COUNT);
      fellBack = results.length > 0;
    }

    this.results = results;
    this.shown = 0;
    this.renderList(true);
    this.renderMarkers(results);
    this.fitTo(results, false);

    if (this.offerNode) this.offerNode.hidden = !fellBack;

    var naam = named.length
      ? this.tCount('name_matches', named.length, { query: this.queryInput ? this.queryInput.value.trim() : '' }) + ' '
      : '';

    if (fellBack) {
      this.setStatus(naam + this.tCount('nearest_instead', results.length, { distance: this.radius }));
    } else if (results.length) {
      var text = origin.label
        ? this.t('results_near', { location: origin.label }) + ' '
        : '';
      this.setStatus(naam + text + this.tCount('results_count', withDistance.length));
    } else {
      this.setStatus(this.t('empty') + ' ' + this.t('empty_hint'));
    }

    // Winkels zonder coordinaten alsnog op de kaart proberen te krijgen.
    if (missing.length) this.resolveMissing(missing);
  };

  /**
   * Zoekt de coordinaten van winkels waar ze ontbreken alsnog op, met een
   * plafond zodat een fout in de data niet honderden calls veroorzaakt.
   */
  StoreLocator.prototype.resolveMissing = function (stores) {
    var self = this;
    if (!this.geocoder) return;

    stores.forEach(function (store) {
      if (store.resolving || self.geocodeBudget <= 0) return;
      store.resolving = true;
      self.geocodeBudget--;

      var address = [store.address, store.postalCode, store.city, store.country]
        .filter(Boolean)
        .join(', ');
      if (!address) return;

      console.warn('[store-locator] winkel zonder coordinaten, wordt opgezocht:', store.name);

      self.geocoder.geocode({ address: address }, function (results, status) {
        if (status !== 'OK' || !results || !results.length) {
          console.warn('[store-locator] coordinaten niet gevonden voor:', store.name);
          return;
        }
        store.lat = results[0].geometry.location.lat();
        store.lng = results[0].geometry.location.lng();
        self.writeCache(self.stores);
        if (self.origin) self.applyResults();
      });
    });
  };

  /* ------------------------------------------------------------ lijst -- */

  StoreLocator.prototype.renderList = function (reset) {
    if (reset) {
      this.listNode.textContent = '';
      this.shown = 0;
    }
    var slice = this.results.slice(this.shown, this.shown + this.perPage);
    var self = this;

    slice.forEach(function (store) {
      self.listNode.appendChild(self.buildListItem(store));
    });
    this.shown += slice.length;

    var hasResults = this.results.length > 0;
    if (this.emptyNode) this.emptyNode.hidden = hasResults;
    if (this.moreButton) this.moreButton.hidden = this.shown >= this.results.length;
  };

  StoreLocator.prototype.renderMore = function () {
    this.renderList(false);
  };

  StoreLocator.prototype.buildListItem = function (store) {
    var self = this;
    var item = el('li', 'store-locator__item');

    var button = el('button', 'store-locator__item-select');
    button.type = 'button';
    button.setAttribute('aria-label', this.t('show_on_map') + ': ' + store.name);
    button.addEventListener('click', function () {
      self.focusStore(store);
    });

    var head = el('span', 'store-locator__item-head');
    head.appendChild(el('span', 'store-locator__item-name', store.name));
    if (typeof store.distance === 'number') {
      head.appendChild(
        el('span', 'store-locator__item-distance', this.t('distance_km', {
          distance: store.distance.toFixed(1)
        }))
      );
    }
    button.appendChild(head);

    var address = el('span', 'store-locator__item-address');
    this.addressLines(store).forEach(function (line) {
      address.appendChild(el('span', 'store-locator__item-line', line));
    });
    button.appendChild(address);
    item.appendChild(button);

    var links = el('p', 'store-locator__item-links');
    this.contactLinks(store).forEach(function (link) {
      links.appendChild(link);
    });
    if (links.childNodes.length) item.appendChild(links);

    return item;
  };

  /**
   * Toont het land als naam in de taal van de bezoeker in plaats van de kale
   * ISO code. Intl zit in elke browser, kan geen kwaad als het ontbreekt.
   */
  StoreLocator.prototype.countryName = function (code) {
    if (!code) return '';
    if (!this.regionNames && this.regionNames !== false) {
      try {
        this.regionNames = new Intl.DisplayNames([this.language], { type: 'region' });
      } catch (err) {
        this.regionNames = false;
      }
    }
    if (!this.regionNames) return code;
    try {
      return this.regionNames.of(code) || code;
    } catch (err) {
      return code;
    }
  };

  StoreLocator.prototype.addressLines = function (store) {
    var lines = [];
    if (store.address) lines.push(store.address);
    if (store.address2) lines.push(store.address2);
    var cityLine = [store.postalCode, store.city].filter(Boolean).join(' ');
    if (cityLine) lines.push(cityLine);
    if (store.country) lines.push(this.countryName(store.country));
    return lines;
  };

  StoreLocator.prototype.contactLinks = function (store) {
    var links = [];

    if (store.phone) {
      var phone = el('a', 'store-locator__link store-locator__link--phone', store.phone);
      phone.href = 'tel:' + store.phone.replace(/[^\d+]/g, '');
      phone.setAttribute('aria-label', this.t('call') + ': ' + store.name);
      links.push(phone);
    }
    if (store.email) {
      var email = el('a', 'store-locator__link store-locator__link--email', this.t('email'));
      email.href = 'mailto:' + store.email;
      links.push(email);
    }
    if (store.website) {
      var site = el('a', 'store-locator__link store-locator__link--website', this.t('website'));
      site.href = store.website;
      site.target = '_blank';
      site.rel = 'noopener noreferrer';
      site.setAttribute('aria-label', this.t('website_external') + ': ' + store.name);
      links.push(site);
    }
    var directions = el('a', 'store-locator__link store-locator__link--directions', this.t('directions'));
    directions.href = this.directionsUrl(store);
    directions.target = '_blank';
    directions.rel = 'noopener noreferrer';
    links.push(directions);

    return links;
  };

  StoreLocator.prototype.directionsUrl = function (store) {
    var destination =
      store.lat !== null && store.lng !== null
        ? store.lat + ',' + store.lng
        : [store.name, store.address, store.postalCode, store.city, store.country]
            .filter(Boolean)
            .join(', ');
    return 'https://www.google.com/maps/dir/?api=1&destination=' + encodeURIComponent(destination);
  };

  /* ---------------------------------------------------------- markers -- */

  StoreLocator.prototype.renderMarkers = function (stores) {
    if (!this.map) return;
    var self = this;

    if (this.clusterer) {
      this.clusterer.clearMarkers();
    } else {
      this.markers.forEach(function (marker) {
        marker.setMap(null);
      });
    }
    this.markers.clear();

    var markers = [];
    stores.forEach(function (store) {
      if (store.lat === null || store.lng === null) return;
      var marker = new google.maps.Marker({
        position: { lat: store.lat, lng: store.lng },
        title: store.name
      });
      marker.addListener('click', function () {
        self.openInfo(store, marker);
      });
      self.markers.set(store.id, marker);
      markers.push(marker);
    });

    var Clusterer = window.markerClusterer && window.markerClusterer.MarkerClusterer;
    if (Clusterer) {
      if (!this.clusterer) {
        this.clusterer = new Clusterer({ map: this.map, markers: markers });
      } else {
        this.clusterer.addMarkers(markers);
      }
    } else {
      markers.forEach(function (marker) {
        marker.setMap(self.map);
      });
    }
  };

  StoreLocator.prototype.fitTo = function (stores, isEurope) {
    if (!this.map) return;
    var points = stores.filter(function (store) {
      return store.lat !== null && store.lng !== null;
    });

    if (!points.length) {
      this.map.setCenter(EUROPE);
      this.map.setZoom(EUROPE_ZOOM);
      return;
    }

    var bounds = new google.maps.LatLngBounds();
    points.forEach(function (store) {
      bounds.extend({ lat: store.lat, lng: store.lng });
    });
    if (this.origin) bounds.extend({ lat: this.origin.lat, lng: this.origin.lng });

    this.map.fitBounds(bounds, isEurope ? 0 : 48);

    // Bij een enkel resultaat zoomt fitBounds veel te ver in.
    var map = this.map;
    google.maps.event.addListenerOnce(map, 'idle', function () {
      if (map.getZoom() > 15) map.setZoom(15);
    });
  };

  StoreLocator.prototype.focusStore = function (store) {
    if (!this.map) return;
    if (store.lat === null || store.lng === null) return;

    this.setView('map');
    this.map.panTo({ lat: store.lat, lng: store.lng });
    if (this.map.getZoom() < 12) this.map.setZoom(13);

    var marker = this.markers.get(store.id);
    if (marker) this.openInfo(store, marker);
  };

  StoreLocator.prototype.openInfo = function (store, marker) {
    if (!this.infoWindow) return;
    this.infoWindow.setContent(this.buildInfoContent(store));
    this.infoWindow.open({ map: this.map, anchor: marker });
  };

  StoreLocator.prototype.buildInfoContent = function (store) {
    var wrapper = el('div', 'store-locator__info');
    wrapper.appendChild(el('strong', 'store-locator__info-name', store.name));

    var address = el('p', 'store-locator__info-address');
    this.addressLines(store).forEach(function (line, index) {
      if (index) address.appendChild(document.createElement('br'));
      address.appendChild(document.createTextNode(line));
    });
    wrapper.appendChild(address);

    if (typeof store.distance === 'number') {
      wrapper.appendChild(
        el('p', 'store-locator__info-distance', this.t('distance_km', {
          distance: store.distance.toFixed(1)
        }))
      );
    }

    var links = el('p', 'store-locator__info-links');
    this.contactLinks(store).forEach(function (link) {
      links.appendChild(link);
    });
    wrapper.appendChild(links);

    return wrapper;
  };

  /* ----------------------------------------------------------- views --- */

  StoreLocator.prototype.setView = function (view) {
    this.root.dataset.view = view;
    this.root.querySelectorAll('[data-sl-view]').forEach(function (button) {
      var active = button.dataset.slView === view;
      button.classList.toggle('store-locator__view--active', active);
      button.setAttribute('aria-pressed', active ? 'true' : 'false');
    });
    if (this.map && view === 'map') {
      google.maps.event.trigger(this.map, 'resize');
    }
  };

  /* ------------------------------------------------------------ start -- */

  function init() {
    document.querySelectorAll('[data-store-locator]').forEach(function (root) {
      if (root.dataset.slReady === 'true') return;
      root.dataset.slReady = 'true';
      new StoreLocator(root);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // De theme editor bouwt een sectie opnieuw op zonder de pagina te herladen.
  document.addEventListener('shopify:section:load', init);
})();
